import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Badge from './Badge.jsx';
import MODULE_BADGES from '../lib/moduleBadges.js';
import NewTicketModal from './NewTicketModal.jsx';
import { useAuth } from '../context/AuthContext.jsx';

// Per-customer renewals table — ported from Mission Control's
// Subscriptions view. One row per site, nearest renewal across the
// site's active modules. Tier ladder: past-due / urgent ≤7 / soon ≤30
// / upcoming ≤60 / current >60.

const TABS = [
  { id: 'all',       label: 'All' },
  { id: 'past-due',  label: 'Past Due' },
  { id: 'urgent',    label: 'Urgent (7d)' },
  { id: 'soon',      label: 'Soon (30d)' },
  { id: 'upcoming',  label: 'Upcoming' },
  { id: 'current',   label: 'Current' }
];

function formatDueDate(iso) {
  if (!iso) return '—';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-CA',
    { year: 'numeric', month: 'short', day: '2-digit', timeZone: 'UTC' });
}
function formatDays(days) {
  if (days == null) return '—';
  if (days < 0) { const a = Math.abs(days); return `${a} ${a === 1 ? 'day' : 'days'} overdue`; }
  if (days === 0) return 'due today';
  if (days === 1) return 'in 1 day';
  return `in ${days} days`;
}
function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  return `$${v.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtRelative(value) {
  if (!value) return 'never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'never';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export default function CustomerRenewalsTab({ contactId }) {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('');
  const [posFilter, setPosFilter] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [ticketSiteId, setTicketSiteId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await axios.get(`/api/schedule/customer/${contactId}/renewals`);
      setSites(res.data.sites || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contactId]);

  const counts = useMemo(() => {
    const c = { all: sites.length };
    for (const t of TABS) if (t.id !== 'all') c[t.id] = 0;
    for (const s of sites) c[s.worstTier] = (c[s.worstTier] || 0) + 1;
    return c;
  }, [sites]);

  const moduleOptions = useMemo(() => {
    const seen = new Map();
    for (const s of sites) for (const m of s.modules) if (!seen.has(m.key)) seen.set(m.key, m.label);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [sites]);
  const posOptions = useMemo(() => {
    const seen = new Set();
    for (const s of sites) if (s.posType) seen.add(s.posType);
    return [...seen].sort();
  }, [sites]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sites.filter((s) => {
      if (tab !== 'all' && s.worstTier !== tab) return false;
      if (moduleFilter && !s.modules.some((m) => m.key === moduleFilter)) return false;
      if (posFilter && s.posType !== posFilter) return false;
      if (q && !`${s.siteName} ${s.siteNumber} ${s.posType}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sites, tab, moduleFilter, posFilter, query]);

  const visibleIds = filtered.map((s) => s.siteId);
  const allSel = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSel = visibleIds.some((id) => selected.has(id));

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSel) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }
  function toggleOne(id) {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function markRenewed(site) {
    setBusyId(site.siteId); setMsg(null);
    try {
      await axios.post('/api/subscriptions/mark-renewed', { site_id: site.siteId });
      setMsg(`${site.siteName}: due renewals rolled forward one year.`);
      await load();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusyId(null); }
  }

  async function sendReminder(site) {
    if (!window.confirm(`Send a renewal reminder for ${site.siteName}?\n\nTest mode is ON — the email is redirected to david@xsitegroup.ca, not the customer.`)) return;
    setBusyId(site.siteId); setMsg(null);
    try {
      const res = await axios.post('/api/subscriptions/send-reminder', { site_id: site.siteId, test_mode: true });
      setMsg(`Test reminder for ${site.siteName} sent to ${res.data.to} (${res.data.modules_count} module(s)).`);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusyId(null); }
  }

  async function sendBulkReminder() {
    const targets = filtered.filter((s) => selected.has(s.siteId));
    if (targets.length === 0) return;
    if (!window.confirm(`Send test renewal reminders for ${targets.length} selected site(s)? (Test mode — redirected to david@xsitegroup.ca.)`)) return;
    setMsg(null);
    let ok = 0;
    for (const s of targets) {
      try { await axios.post('/api/subscriptions/send-reminder', { site_id: s.siteId, test_mode: true }); ok++; } catch { /* skip */ }
    }
    setMsg(`Sent ${ok} of ${targets.length} test reminder(s).`);
  }

  function exportSelected() {
    const targets = filtered.filter((s) => selected.has(s.siteId));
    if (targets.length === 0) return;
    const head = ['Site', 'POS', 'Tier', 'Next Charge', 'Days', 'Amount Due', 'Annual Total', 'Modules'];
    const rows = targets.map((s) => [
      s.siteName, s.posType, s.worstTier, s.nearestRenewalDate || '', s.nearestDays ?? '',
      s.totalDue, s.totalAnnual, s.modules.map((m) => m.label).join('; ')
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `renewals_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  if (loading) return <p className="modal-sub">Loading renewals…</p>;

  return (
    <div className="renewals-view">
      {error && <p className="error">{error}</p>}
      {msg && <p className="save-message">{msg}</p>}

      {/* Filter tabs */}
      <div className="renewals-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" className={`renewals-tab ${tab === t.id ? 'renewals-tab-active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}<span className="renewals-tab-count">{counts[t.id] || 0}</span>
          </button>
        ))}
      </div>

      {/* Dropdowns + search */}
      <div className="toolbar">
        <input type="search" className="search-input" placeholder="Search site, number, POS…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <select className="layout-dropdown" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
          <option value="">All Modules</option>
          {moduleOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <select className="layout-dropdown" value={posFilter} onChange={(e) => setPosFilter(e.target.value)}>
          <option value="">All POS</option>
          {posOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="result-count cell-mono">{filtered.length} {filtered.length === 1 ? 'site' : 'sites'}</span>
      </div>

      <div className="table-wrap">
        <table className="data-table renewals-table">
          <thead>
            <tr>
              <th className="th-statusbar" />
              <th className="th-checkbox">
                <input type="checkbox" aria-label="Select all" checked={allSel}
                  ref={(n) => { if (n) n.indeterminate = someSel && !allSel; }} onChange={toggleAll} disabled={visibleIds.length === 0} />
              </th>
              <th>Site</th>
              <th>Modules</th>
              <th>Next Charge</th>
              <th>Days</th>
              <th className="th-num">Amount</th>
              <th>Last Activity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="empty-row">No sites match the current filters.</td></tr>
            )}
            {filtered.map((s) => {
              const isSel = selected.has(s.siteId);
              const hasDue = s.modules.some((m) => m.tier !== 'current');
              return (
                <tr key={s.siteId} className={isSel ? 'row-selected' : ''}>
                  <td className="td-statusbar"><span className={`tier-bar tier-bar-${s.worstTier}`} /></td>
                  <td className="td-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleOne(s.siteId)} aria-label={`Select ${s.siteName}`} />
                  </td>
                  <td>
                    <div className="cell-primary">{s.siteName}</div>
                    <div className="cell-subtext cell-mono">{[s.siteNumber, s.posType].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td>
                    <span className="renewals-modules">
                      {s.modules.slice(0, 6).map((m) => {
                        const b = MODULE_BADGES[m.key];
                        return <Badge key={m.key} tone={b?.tone || 'grey'}>{b?.abbr || m.key.slice(0, 3).toUpperCase()}</Badge>;
                      })}
                      {s.modules.length > 6 && <span className="renewals-more" title={s.modules.map((m) => m.label).join(', ')}>+{s.modules.length - 6}</span>}
                    </span>
                  </td>
                  <td className={`cell-mono tier-text-${s.worstTier}`}>{formatDueDate(s.nearestRenewalDate)}</td>
                  <td className={`cell-mono tier-text-${s.worstTier}`}>{formatDays(s.nearestDays)}</td>
                  <td className="th-num">
                    <div className="renewals-amount">
                      {hasDue ? <span className={`tier-text-${s.worstTier}`}>{money(s.totalDue)} due</span> : <span className="cell-muted">—</span>}
                      <span className="renewals-annual cell-mono">{money(s.totalAnnual)} annual</span>
                    </div>
                  </td>
                  <td className="cell-mono cell-muted">{fmtRelative(s.lastActivity)}</td>
                  <td className="renewals-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="btn-secondary btn-sm" disabled={busyId === s.siteId} onClick={() => sendReminder(s)}>Send Reminder</button>
                    <button type="button" className="btn-primary btn-sm" disabled={busyId === s.siteId} onClick={() => markRenewed(s)}>Mark Renewed</button>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => setTicketSiteId(s.siteId)}>+ Ticket</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="bulk-bar">
          <span className="cell-mono">{selected.size} site{selected.size === 1 ? '' : 's'} selected</span>
          <button type="button" className="btn-primary btn-sm" onClick={sendBulkReminder}>Send Bulk Reminder</button>
          <button type="button" className="btn-secondary btn-sm" onClick={exportSelected}>Export Selected</button>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      <NewTicketModal
        open={ticketSiteId != null}
        preselectedSiteId={ticketSiteId}
        currentUserId={user?.id}
        onCancel={() => setTicketSiteId(null)}
        onCreated={() => { setTicketSiteId(null); setMsg('Ticket created.'); }}
      />
    </div>
  );
}
